<script>
    import { select } from "d3";
    
    let scrollables = {};
    function makeScrollable(name, scrollFn, initFn, stopFn) {
        /* console.log("makeScrollable"); */
        var slides = select(".slides");
        var s = slides.selectAll(".scrollable-" + name).data([{}]);

        // If the div doesn't exist create it;
        s = s
            .enter()
            .append("div")
            .attr("class", "scrollable scrollable-" + name)
            .merge(s);

        // Default initFn
        initFn =
            initFn || function () {};

        // Default scrollFn
        scrollFn =
            scrollFn ||
            function (step) {
                s.select("text")
                    .transition()
                    .duration(1000)
                    .text("Step=" + step);
            };
        stopFn = stopFn || function() {};

        // Bind the this element
        initFn = initFn.bind(s.node());
        scrollFn = scrollFn.bind(s.node());
        stopFn = stopFn.bind(s.node());

        // Run the init code
        initFn();

        // Add the scrollable to the list
        scrollables[name] = {
            scroll: scrollFn,
            stop: stopFn,
        }; 
    }

    import * as hagrid from "@saehrimnir/hagrid";
    import * as d3 from "d3";

    import { getScales } from "../utils.js";
    import { morph_c, morph_d, morph_xy } from "../custom_actions.js";

    import { reveal } from "../stores/store.js";
    import { onMount } from "svelte";
    import { fade, fly } from "svelte/transition";

    $reveal.addEventListener("slidechanged", function (event) {
        var scrollable;

        if (d3.select(event.currentSlide).classed("scroll")) {
            if (
                d3.select(event.currentSlide).attr("step") &&
                d3.select(event.currentSlide).attr("scrollable")
            ) {
                scrollable = d3.select(event.currentSlide).attr("scrollable");
                
                d3.select(".scrollable-" + scrollable)
                    .style("visibility", "visible")
                    .style("opacity", 1);
                d3.select(".reveal").classed("scrolling", true);
                if (scrollable in scrollables) scrollables[scrollable].scroll(d3.select(event.currentSlide).attr("step"));
            }
        } else {
            // Not scrolling

            // Stop all scrollables
            for (var s in scrollables) {
                scrollables[s].stop();
            }
            d3.selectAll(".scrollable").style("visibility", "hidden").style("opacity", 0);
            d3.select(".reveal").classed("scrolling", false);
        }
    });

    const W = 500;
    const M = 40;
    const range = [M, W - M];

    let step;
    function doStep(event) {
        console.log("method do step", event)
        if (event == "step 0" || event == "step 3") {
            level = 1;
        } else if (event == "step 1" || event == "step 4") {
            level = 2;
        } else if (event == "step 2") {
            level = 3;
        }
        step = event;
    }

    onMount(async () => {
        makeScrollable("method", doStep);
    })

    let fly_transition = { duration: 2000}
    let level = 2;
    $: hilbert_size = 2 ** level;
    $: hilbert_length = hilbert_size ** 2;
    $: hilbert_curve = d3.range(0, hilbert_length).map(i => hagrid.hilbert_decode(i, hilbert_size));

    $: hilbert_x = d3.scaleLinear().domain([-.5, hilbert_size - .5]).range(range);

    $: gosper_length = 7 ** Math.min(2, level);
    $: gosper_curve = hagrid.gosper_curve(level)//d3.range(0, gosper_length).map(i => hagrid.gosper_decode(i, level + 1));
    //$: console.log("gosper_curve", gosper_curve)
    $: gosper_scales = getScales(gosper_curve, [range, range], true)

    $: curve = step && (+(step.slice(-1)) >= 3) 
                    ? gosper_curve.map(d => [gosper_scales.x(d[0]), gosper_scales.y(d[1])]) 
                    : hilbert_curve.map(d => d.map(hilbert_x));
    $: color_scale = d3.scaleSequential(d3.interpolateWarm).domain([0, curve.length])
    $: which_curve = step && (+(step.slice(-1)) >= 3)  ? "gosper" : "hilbert"
</script>

<!-- <section>
    <h2>Hagrid</h2>
    <p>a method to gridify scatterplots using hilbert and gosper curves</p>
</section> -->

<section>
    <h2>Hilbert & Gosper Curve</h2>

    <section>
        <p class="fragment r-fit-text"><u>H</u>ilbert <u>a</u>nd <u>G</u>osper Curve-based G<u>rid</u>s</p>
        <ul class="fragment">
            <li>self-avoiding and self-similar,</li>
            <li>recursive construction rules,</li>
            <li>neighborhood-preserving, and</li>
            <li>bijective mapping functions exists</li>
        </ul>
        <aside class="notes">
            For gridifying the scatterplots in the examples before, we used our technique called hagrid, which stands for ✨
            "Hilbert and gosper curve-based grids" ✨
            The hilbert and the gosper curve are space filling curves, with the properties of being self-avoiding and
            self-similar, they have recursive construction rules, are neighborhood-preserving, and
            have a bijective mapping function from the curve to 2d.
        </aside>
    </section>
    
    <section class="scroll" scrollable="method" step="step 0">
        hilbert curve at level 1
        <aside class="notes">
            The starting pattern of the hilbert curve has an U shape.
        </aside>
    </section>
    <section class="scroll" scrollable="method" step="step 1">
        hilbert curve at level 2
        <aside class="notes">
            at the next level, the starting pattern gets rotated and repeated four times. 
        </aside>
    </section>
    <section class="scroll" scrollable="method" step="step 2">
        hilbert curve at level 3
        <aside class="notes">
            at the next level, the last pattern gets rotated and repeated again.
        </aside>
    </section>
    <section class="scroll" scrollable="method" step="step 3">
        gosper curve at level 1
        <aside class="notes">
            a starting pattern of the gosper curve looks like this
        </aside>
    </section>
    <section class="scroll" scrollable="method" step="step 4">
        gosper curve at level 2
        <aside class="notes">
            at the next level, the starting pattern gets rotated and repeated seven times.
        </aside>
    </section>
</section>

<div class="scrollable scrollable-method">
    <div>
        {#if step}
        <svg width={W} height={W}>
            <defs>
                <marker id="pointer" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="white" />
                </marker>
            </defs>
            <g fill="white" text-anchor="middle" dominant-baseline="central" font-size=20 font-weight="900 !important" >
                {#key which_curve}
                    {#each d3.pairs(curve) as [p1, p2], i}
                        <path transition:fly={fly_transition} 
                            use:morph_d={{points: [p1, p2]}}
                            fill="none" 
                            stroke={color_scale(i + .5)} 
                            stroke-width=5 />
                    {/each}

                    {#each curve as p, i}
                        <circle transition:fly={fly_transition} 
                            use:morph_c={{point: p}} 
                            r=8 
                            fill={color_scale(i)} />
                        <text transition:fly={fly_transition} 
                            use:morph_xy={{pos: p.map(v => v - 20)}} 
                            fill="white">
                            {i}
                        </text>
                    {/each}
                {/key}

            </g>
        </svg>
        {/if}
    </div>
</div>